<div
    class="prom-date-picker"
    role="dialog"
    aria-modal="true"
    aria-label={i18n.datepickerTitle}
    on:keydown={onKeyPress}
>
    <div class="prom-date-picker__header">
        <button type="button" class="prom-date-picker__previous-month prom-date-picker__icon" on:click={(e) => changePage(-1, e)}>
            <span class="screen-reader">{i18n.previousMonth}</span>
        </button>
        <span>
            {i18n['month' + selectorStartDate.getMonth()]}
            {selectorStartDate.getFullYear()}
        </span>
        <button type="button" class="prom-date-picker__next-month prom-date-picker__icon" on:click={(e) => changePage(1, e)}>
            <span class="screen-reader">{i18n.nextMonth}</span>
        </button>
    </div>
    <table bind:this={calendarElement} class="prom-date-picker__calendar">
        <thead class="prom-date-picker__calendar-row">
            <th class="prom-date-picker__calendar-header" >{i18n.week}</th>
            {#each daysOfWeek as day}
                <th class="prom-date-picker__calendar-header" >{i18n['day' + day]}</th>
            {/each}
        </thead>
        {#each weeksToShow as week (week[0].id)}
            <tr class="prom-date-picker__calendar-row">
                <td class="prom-date-picker__calendar-cell-week">
                    <span class="screen-reader">
                        {i18n.week}
                    </span>
                    <span class="prom-date-picker__week">
                        {week[0].weekNumber}
                    </span>
                </td>
                {#each week as day (day.id)}
                    <td class="prom-date-picker__calendar-cell">
                        <button
                            type="button"
                            class="prom-date-picker__day prom-date-picker__{day.id}"
                            class:prom-date-picker__day--today={day.isToday}
                            class:prom-date-picker__day--selected-month={day.isThisMonth}
                            class:prom-date-picker__day--selected={isSameDate(day.date, value)}
                            class:prom-date-picker__day--highlighted={day.highlighted}
                            tabindex={isSameDate(day.date, value) ? 0 : -1}
                            aria-label={day.dateString}
                            aria-describedby="day-{day.id}-description"
                            on:click={(e) => setValue(day.date, e)}
                        >
                            {day.date.getDate()}

                            <span id="day-{day.id}-description" class="screen-reader">
                                {#if day.highlighted}
                                    {i18n.highlightedDateDescription}
                                {/if}
                            </span>
                        </button>
                    </td>
                {/each}
            </tr>
        {/each}
    </table>
    <div class="prom-date-picker__footer">
        <button class="prom-date-picker__today" type="button" on:click={setValueToday}>
            {i18n.today}
        </button>
        {#if highlightedDates && highlightedDates.length}
            <div class="prom-date-picker__highlight-legend">
                <span>{i18n.highlightedDatesLabel}</span>
            </div>
        {/if}
    </div>
</div>

<script>
    import { normalizeKey } from '@itslearning/atlas/keyboard/normalize';
    import { Keys } from '@itslearning/atlas/keyboard/keys';
    import { idForDay, isSameDate } from './dateHelpers';
    import { createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();

    export let value = null;
    export let firstDayOfWeek = 1;
    export let selectorStartDate = new Date();
    export let locale = 'en-US';
    export let i18n = {};
    export let highlightedDates = [];

    /**
     * @description Gets week number from a given input date
     * @param {Date} inputDate - The date to get the week number from.
     */
    function getWeek(inputDate) {
            const date = new Date(Date.UTC(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate()));
            const dayNumber = date.getUTCDay() || 7;
            date.setUTCDate(date.getUTCDate() + 4 - dayNumber);
            const firstDayOfYear = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
            return Math.ceil((((+date - +firstDayOfYear) / 86400000) + 1) / 7)
    }

    let calendarElement;

    $: if (value) {
        selectorStartDate = new Date(value.getFullYear(), value.getMonth(), 1);
    }

    let weeksToShow;

    $: {
        const firstDateOfMonth = new Date(selectorStartDate.getFullYear(), selectorStartDate.getMonth(), 1);

        let diff = firstDayOfWeek - firstDateOfMonth.getDay();

        if (diff > 0) {
            diff -= 7;
        }

        const firstDateToDisplay = new Date(firstDateOfMonth);

        firstDateToDisplay.setDate(firstDateOfMonth.getDate() + diff);

        const day = new Date(firstDateToDisplay);
        const weeks = [];
        const today = new Date();

        for (let i = 0; i < 6; i++) {
            const week = [];

            for (let i = 0; i < 7; i++) {
                const date = new Date(day);
                week.push({
                    id: idForDay(date),
                    date,
                    isToday: isSameDate(date, today),
                    isThisMonth: date.getMonth() === firstDateOfMonth.getMonth(),
                    dateString: date.toLocaleDateString(locale, { dateStyle: 'full' }),
                    weekNumber: getWeek(date),
                    weekString: i18n.week + getWeek(date),
                    highlighted: highlightedDates.some(i => isSameDate(i, date)),
                });

                day.setDate(day.getDate() + 1);
            }

            if (week.some(day => day.isThisMonth)) {
                weeks.push(week);
            }
        }

        weeksToShow = weeks;
    }

    const days = [0, 1, 2, 3, 4, 5, 6];

    $: daysOfWeek = firstDayOfWeek === 0 ? days : [...days.slice(firstDayOfWeek), ...days.slice(0, firstDayOfWeek)];

    function changePage(months, event) {
        const newSelectorStartDate = new Date(selectorStartDate);

        newSelectorStartDate.setDate(1);
        newSelectorStartDate.setMonth(selectorStartDate.getMonth() + months);

        selectorStartDate = newSelectorStartDate;

        // If keyboard click
        if (event.screenX === 0 && event.screenY === 0) {
            setValue(selectorStartDate, event);
        }
    }

    function setValue(date, event) {
        value = date;

        const selector = `.prom-date-picker__${idForDay(date)}`;

        setTimeout(() => {
            calendarElement.querySelector(selector).focus();
            dispatch('select', event);
        }, 0);
    }

    function setValueToday(event) {
        setValue(new Date(), event);
    }

    function onKeyPress(event) {
        const { key } = event;

        const newValue = value ? new Date(value) : new Date();

        switch (normalizeKey(key)) {
            case Keys.DOWN:
                newValue.setDate(newValue.getDate() + 7);
                break;
            case Keys.UP:
                newValue.setDate(newValue.getDate() - 7);
                break;
            case Keys.RIGHT:
                newValue.setDate(newValue.getDate() + 1);
                break;
            case Keys.LEFT:
                newValue.setDate(newValue.getDate() - 1);
                break;
            case Keys.PAGEUP:
                newValue.setDate(1);
                newValue.setMonth(newValue.getMonth() - 1);
                break;
            case Keys.PAGEDOWN:
                newValue.setDate(1);
                newValue.setMonth(newValue.getMonth() + 1);
                break;
            case Keys.ESCAPE:
                break;
            case Keys.SPACE:
            case Keys.ENTER:
                if (!calendarElement.contains(event.target)) {
                    return;
                }

                break;
            default:
                return;
        }

        event.preventDefault();
        setValue(newValue, event);
    }
</script>
